import { ApiClientError } from './api-error';

const ERROR_MESSAGES: Record<string, string> = {
  AUTH_TOKEN_INVALID: '인증 정보가 유효하지 않습니다. 다시 로그인해 주세요.',
  AUTH_TOKEN_EXPIRED: '로그인 시간이 만료되었습니다. 다시 로그인해 주세요.',
  AUTH_INVALID_CREDENTIALS: '아이디 또는 비밀번호가 올바르지 않습니다.',
  AUTH_ACCOUNT_LOCKED: '계정이 일시적으로 잠겼습니다. 잠시 후 다시 시도해 주세요.',
  AUTH_REFRESH_INVALID: '로그인 세션이 만료되었습니다. 다시 로그인해 주세요.',
  AUTH_USER_INACTIVE: '현재 로그인할 수 없는 계정입니다.',
  RBAC_FORBIDDEN: '접근 권한이 없습니다.',
  RESOURCE_NOT_FOUND: '요청한 데이터를 찾을 수 없습니다.',
  RESOURCE_CONFLICT: '이미 존재하거나 충돌하는 데이터입니다.',
  VALIDATION_FAILED: '입력값이 올바르지 않습니다.',
  SYSTEM_INTERNAL: '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
};

/**
 * 사용자에게 노출할 에러 메시지.
 *
 * 표준 ApiClientError 는 code catalog 를 우선하고, legacy axios / 일반 Error 는
 * 기존 화면들이 사용하던 message 추출 규칙을 유지한다.
 */
export function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    const specificMessage = err.message && err.message !== err.code ? err.message : undefined;
    const hasFieldErrors = Array.isArray(err.fields) && err.fields.length > 0;

    // VALIDATION_FAILED 중 class-validator 의 field 단위 에러는 폼에 매핑되므로
    // 토스트는 코드 카탈로그 메시지를 쓴다. 하지만 roles/auth-change-password 등
    // 수동 검증 엔드포인트가 field 없이 구체적 사유를 top-level message 로 내려주는
    // 경우에는 그 메시지를 그대로 노출해야 사용자가 원인을 알 수 있다.
    const message =
      err.code === 'VALIDATION_FAILED' && !hasFieldErrors && specificMessage
        ? specificMessage
        : (ERROR_MESSAGES[err.code] ?? specificMessage ?? fallback);

    return withTraceId(message, err.traceId);
  }

  if (err && typeof err === 'object') {
    const legacy = err as {
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const apiMessage = legacy.response?.data?.message;
    if (typeof apiMessage === 'string') return apiMessage;
    if (Array.isArray(apiMessage) && typeof apiMessage[0] === 'string') return apiMessage[0];
    if (typeof legacy.message === 'string') return legacy.message;
  }

  return fallback;
}

function withTraceId(message: string, traceId: string | undefined): string {
  if (!traceId) return message;
  return `${message} (추적번호: ${traceId})`;
}
